import React from "react";
import { motion } from "framer-motion";
import { CASE_OUTCOMES } from "@/data/cases";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import CursorGlowCard from "@/components/brand/CursorGlowCard";

export default function CaseOutcomes() {
  const [feature, ...rest] = CASE_OUTCOMES;
  return (
    <section
      data-testid="case-outcomes-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-deeper"
    >
      <AmbientLight size={900} color="gold" intensity={0.28} style={{ top: "8%", left: "-10%" }} />
      <AmbientLight size={1000} color="blue" intensity={0.3} style={{ bottom: "0%", right: "-5%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="10" label="Representative Recoveries" className="mb-14" />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mb-20 lg:mb-24 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
              Case outcomes,
              <br />
              <span className="gold-metallic font-display font-light">presented as facts.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
          </div>
          <div className="lg:col-span-5">
            <p className="text-ag-text-secondary text-base lg:text-[1.05rem] leading-[1.75] font-light max-w-md">
              Anonymized recoveries from the firm&apos;s Florida practice. Claim type, location, the challenge,
              and the outcome. No dramatic language.
            </p>
          </div>
        </div>

        {/* Featured case — editorial spotlight */}
        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="glass-strong relative p-7 sm:p-10 lg:p-16 mb-4 sm:mb-6 overflow-hidden group"
          data-testid={`case-${feature.id}`}
          style={{
            boxShadow:
              "inset 0 1px 0 rgba(198,168,107,0.15), 0 50px 100px -30px rgba(0,119,255,0.25)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div
            className="pointer-events-none absolute -right-32 -top-32 w-[480px] h-[480px] rounded-full opacity-50"
            style={{ background: "radial-gradient(circle, rgba(198,168,107,0.12), transparent 60%)" }}
          />
          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-[0.6rem] text-gold tracking-[0.32em] uppercase">Featured Recovery</span>
                <div className="h-px w-12 bg-gold/40" />
              </div>
              <div className="font-mono text-[0.62rem] text-ag-text-muted tracking-[0.28em] uppercase mb-4">
                {feature.claim_type} · {feature.location} · {feature.year}
              </div>
              <h3 className="font-display text-2xl lg:text-[2rem] text-white leading-[1.2] font-light tracking-[-0.025em]">
                {feature.challenge}
              </h3>
            </div>
            <div className="lg:col-span-7 lg:border-l lg:border-white/[0.08] lg:pl-16">
              <div className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.32em] uppercase mb-6">
                Outcome
              </div>
              <p className="font-display-tight gold-metallic text-[clamp(2.25rem,4.4vw,3.75rem)] leading-[1.02] tracking-[-0.045em] font-light">
                {feature.outcome}
              </p>
            </div>
          </div>
        </motion.article>

        {/* Supporting cases grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {rest.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <CursorGlowCard
                as="article"
                className="p-9 lg:p-10 hover:border-gold/30 group h-full"
                data-testid={`case-${c.id}`}
                glowColor="rgba(198,168,107,0.15)"
              >
                <div className="relative h-full flex flex-col">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-[0.6rem] text-gold tracking-[0.3em] uppercase">
                      {c.claim_type}
                    </span>
                    <span className="font-mono text-[0.58rem] text-ag-text-muted">/ {c.year}</span>
                  </div>
                  <div className="text-[0.72rem] text-ag-text-muted mb-4 font-mono tracking-[0.22em] uppercase">
                    {c.location}
                  </div>
                  <h3 className="font-display text-lg lg:text-xl text-white leading-[1.35] mb-8 font-light tracking-[-0.02em] flex-1">
                    {c.challenge}
                  </h3>
                  <div className="pt-6 border-t border-white/[0.08]">
                    <div className="font-mono text-[0.58rem] text-ag-text-muted tracking-[0.28em] uppercase mb-3">
                      Outcome
                    </div>
                    <p className="font-display-tight gold-metallic text-xl lg:text-2xl leading-[1.1] tracking-[-0.035em] font-light">
                      {c.outcome}
                    </p>
                  </div>
                </div>
              </CursorGlowCard>
            </motion.div>
          ))}
        </div>

        <p className="text-center mt-20 text-ag-text-muted text-[0.7rem] font-mono tracking-[0.32em] uppercase">
          Past results do not guarantee future outcomes
        </p>
      </div>
    </section>
  );
}
